import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { UserDb } from 'services/user-db.service';
import { DocWatcher } from 'services/database-wrapper';
import { CoachRelationship, coachRelAsClientId } from '@cf-shared/data-model/coach';
import { LoggingService } from 'services/logging.service';
import { LL } from 'i18n/i18n.service';

/**
 * Holding page for option B of coach mode (#3389). After the client
 * finishes the coached intake wizard (steps 1-8 through Goals), the
 * intake wizard sets plan.welcomePhase = AwaitingCoachFinalize and
 * navigates here. The client stays on this page until their coach
 * finishes the review, generates the plan, and the welcomePhase flips
 * to Done — at which point app.component routes them to the home page.
 *
 * The page displays the coach's name (from the
 * coach-relationship/as-client doc) and a plain-language "your coach
 * is finalizing your program" message. The rest of the app is
 * inaccessible from here because app.component's routing always sends
 * an AwaitingCoachFinalize plan back to this page on app boot.
 */
@Component({
  selector: 'page-awaiting-coach',
  templateUrl: 'awaiting-coach.html',
  host: { 'class': 'awaiting-coach' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AwaitingCoachPage extends CsPage<{}> implements OnDestroy {
  protected LL = LL;
  coachName = '';
  // Tier 1 white-labeling (#3707): coach logo + brand name for this surface.
  logoUrl = '';
  brandName = '';
  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private logging: LoggingService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewWillEnter() {
    // subscribe the page-level watcher to the singleton, then call
    // loadCoachName once. getSingleton wires the singleton's change
    // notifications into this.docWatcher, so when the doc later syncs
    // in from the coach's database the callback re-reads the name and
    // re-renders.
    this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
      if (this.viewDestroyed) return;
      this.loadCoachName();
      this.cd.markForCheck();
    });
    this.loadCoachName();
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  private loadCoachName() {
    try {
      const relDoc = this.userDb.database.getSingleton(coachRelAsClientId, this.docWatcher) as CoachRelationship;
      this.coachName = relDoc?.coachName || '';
      this.logoUrl = relDoc?.branding?.logoUrl || '';
      this.brandName = relDoc?.branding?.brandName || '';
    } catch (err) {
      // the singleton may not be initialized yet on a brand-new client db
      // (claim has not landed). track so a real failure surfaces in Sentry
      // rather than silently keeping the empty fallback forever; fall back
      // to the generic-message branch in the template.
      this.coachName = '';
      this.logoUrl = '';
      this.brandName = '';
      this.logging.trackSilentProblem('AwaitingCoachPage: getSingleton coach-relationship/as-client threw', {
        err: (err as Error)?.message || String(err),
      });
    }
  }
}
